import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  Box,
  Flex,
  FlexProps,
  IconButton,
  Input,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
// eslint-disable-next-line no-restricted-imports -- 新しいコンポーネントを使う
import { CalendarComponent } from 'src/components/__legacy__/form/calendar'
// eslint-disable-next-line no-restricted-imports -- 新しいコンポーネントを使う
import { Spacer } from 'src/components/__legacy__/spacer/spacer'
import { Calendar } from 'src/components/icon'
import { Select, SelectItem } from 'src/components/Select/Select'
import { zeroPadding } from 'src/utils/zeroPadding'

type Props = {
  value: string
  onChange: (value: string) => void
  'aria-labelledby'?: string
  setUnderEditSchedule?: (value: boolean) => void
}

type ScheduleValue = {
  date: string
  hour: string
  minute: string
}

// 0 ~ 23
const hours = [...Array(24)].map((_, i) => zeroPadding(String(i), 2))
// 00 ~ 55
const minutes = [...Array(60 / 5)].map((_, i) => zeroPadding(String(i * 5), 2))

const hoursSelectItems: SelectItem[] = hours.map(hour => ({
  label: hour,
  value: hour,
}))
const minutesSelectItems: SelectItem[] = minutes.map(minute => ({
  label: minute,
  value: minute,
}))

const formatScheduleValue = (
  date: string,
  hour: string,
  minute: string,
): string => {
  const joined = `${date} ${hour}:${minute}`
  return dayjs(joined).isValid() ? dayjs(joined).format() : ''
}

const hasCompleteScheduleValue = (scheduleValue: ScheduleValue): boolean => {
  return (
    scheduleValue.date !== '' &&
    scheduleValue.hour !== '' &&
    scheduleValue.minute !== ''
  )
}

export const ScheduleInput: React.FC<Props> = ({
  value,
  onChange,
  setUnderEditSchedule,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [date, setDate] = useState<string>(
    dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '',
  )
  const hourValue = value ? dayjs(value).format('HH') : ''
  const minuteValue = value ? dayjs(value).format('mm') : ''
  const [hour, setHour] = useState<string>(hourValue)
  const [minute, setMinute] = useState<string>(minuteValue)

  const onChangeDate = (dateValue: string) => {
    setDate(dateValue)
    setUnderEditSchedule?.(true)
    if (
      hasCompleteScheduleValue({
        date: dateValue,
        hour: hour,
        minute: minute,
      })
    ) {
      onChange(formatScheduleValue(dateValue, hour, minute))
    }
  }
  const onChangeDateValue = (date: Date) => {
    onChangeDate(dayjs(date).format('YYYY-MM-DD'))
  }

  const onChangeHour = (hourValue: string) => {
    setHour(hourValue)
    setUnderEditSchedule?.(true)
    if (
      hasCompleteScheduleValue({
        date: date,
        hour: hourValue,
        minute: minute,
      })
    ) {
      onChange(formatScheduleValue(date, hourValue, minute))
    }
  }
  const onChangeMinute = (minuteValue: string) => {
    setMinute(minuteValue)
    setUnderEditSchedule?.(true)
    if (
      hasCompleteScheduleValue({
        date: date,
        hour: hour,
        minute: minuteValue,
      })
    ) {
      onChange(formatScheduleValue(date, hour, minuteValue))
    }
  }
  const iconButtonRef = useRef<HTMLButtonElement>(null)

  const [calendarPosition, setCalendarPosition] = useState<FlexProps>()

  const calculateCalendarPosition = useCallback(() => {
    if (!iconButtonRef.current) return
    // アイコンボタンと右端を揃えつつ、下に8pxの余白を持たせる
    const right =
      window.innerWidth - iconButtonRef.current.getBoundingClientRect().right
    const top = iconButtonRef.current.getBoundingClientRect().bottom + 8
    setCalendarPosition({ right, top })
  }, [])

  useEffect(() => {
    calculateCalendarPosition()
  }, [calculateCalendarPosition])

  useEffect(() => {
    window.addEventListener('resize', calculateCalendarPosition)
    return () => {
      window.removeEventListener('resize', calculateCalendarPosition)
    }
  }, [calculateCalendarPosition])

  return (
    <Box aria-labelledby={ariaLabelledBy}>
      <Flex align="center">
        <Stack
          display="inline-block"
          pos="relative"
          spacing="2"
          overflow="visible"
        >
          <Flex align="center" w="full" h="max-content">
            <Flex align="center" mr="4">
              <Input
                w="72px"
                mr="1"
                value={dayjs(date).isValid() ? dayjs(date).format('YYYY') : ''}
                readOnly
              />
              <Text as="span" fontSize="sm" fontWeight="bold" mr="3">
                年
              </Text>
              <Input
                w="52px"
                mr="1"
                value={dayjs(date).isValid() ? dayjs(date).format('MM') : ''}
                readOnly
              />
              <Text as="span" fontSize="sm" fontWeight="bold" mr="3">
                月
              </Text>
              <Input
                w="52px"
                mr="1"
                value={dayjs(date).isValid() ? dayjs(date).format('DD') : ''}
                readOnly
              />
              <Text as="span" fontSize="sm" fontWeight="bold">
                日
              </Text>
            </Flex>
            <IconButton
              ref={iconButtonRef}
              variant="customIconButtonGhost"
              aria-label="calendar icon"
              icon={<Calendar />}
              onClick={onOpen}
              pos="relative"
            />
          </Flex>
          {isOpen && (
            <Flex pos="fixed" zIndex="1" {...calendarPosition}>
              <CalendarComponent
                onChange={onChangeDateValue}
                onClose={onClose}
              />
            </Flex>
          )}
        </Stack>
        <Spacer size={24} horizontal />
        <Flex align="end">
          <Select
            items={hoursSelectItems}
            value={hour}
            onChange={onChangeHour}
            width={80}
            placeholder="--"
          />
          <Spacer size={8} horizontal />
          <Text fontSize="sm">時</Text>
          <Spacer size={8} horizontal />
          <Select
            items={minutesSelectItems}
            value={minute}
            onChange={onChangeMinute}
            width={80}
            placeholder="--"
          />
          <Spacer size={8} horizontal />
          <Text fontSize="sm">分</Text>
        </Flex>
      </Flex>
    </Box>
  )
}
